
import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const isProduction = import.meta.env.PROD;
const DOMAIN = 'gotrekking.in';

// Initialize Mixpanel with proper configuration and handle errors
try {
  // Initialize only if we have a token
  if (MIXPANEL_TOKEN) {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: !isProduction,
      track_pageview: false, // We'll handle this manually for more control
      persistence: 'localStorage',
      ignore_dnt: true,
      api_host: `https://api.mixpanel.com`,
    });

    // Log initialization for debugging
    console.log(`[Analytics] Initialized with token: ${MIXPANEL_TOKEN ? MIXPANEL_TOKEN.substring(0, 4) + '***' : 'No token provided'}`);
    console.log(`[Analytics] Environment: ${isProduction ? 'Production' : 'Development'}`);
    console.log(`[Analytics] Debug mode: ${!isProduction}`);

    // Set global properties for all events
    mixpanel.register({
      'App Version': '1.0.0',
      'Platform': 'Web',
      'Domain': DOMAIN
    });
  } else {
    console.warn('[Analytics] No Mixpanel token provided, analytics will be disabled');
  }
} catch (error) {
  console.error('[Analytics] Failed to initialize Mixpanel:', error);
}

// Create a safe version of mixpanel that won't crash if initialization failed
const safelyCallMixpanel = (method: keyof typeof mixpanel, ...args: any[]) => {
  try {
    if (MIXPANEL_TOKEN && mixpanel[method] && typeof mixpanel[method] === 'function') {
      return (mixpanel[method] as Function)(...args);
    }
  } catch (error) {
    console.error(`[Analytics] Error calling ${String(method)}:`, error);
  }
};

// Analytics wrapper to standardize tracking
const Analytics = {
  // Track specific events
  track: (event: string, properties?: Record<string, any>) => {
    try {
      console.log(`[Analytics] ${event}`, properties);
      
      // Return early if no token
      if (!MIXPANEL_TOKEN) return;
      
      const enhancedProps = {
        ...properties,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        referrer: document.referrer || 'direct',
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      };
      
      safelyCallMixpanel('track', event, enhancedProps);
    } catch (error) {
      console.error(`[Analytics] Error tracking event ${event}:`, error);
    }
  },

  // Track page views
  pageView: (pageName: string, properties?: Record<string, any>) => {
    try {
      const pageProps = {
        page: pageName,
        domain: DOMAIN,
        ...properties,
      };
      
      console.log(`[Analytics] Page View: ${pageName}`, pageProps);
      
      // Return early if no token
      if (!MIXPANEL_TOKEN) return;
      
      safelyCallMixpanel('track', 'Page View', pageProps);
    } catch (error) {
      console.error(`[Analytics] Error tracking page view ${pageName}:`, error);
    }
  },

  // Identify users when they log in
  identify: (userId: string, traits?: Record<string, any>) => {
    try {
      // Return early if no token
      if (!MIXPANEL_TOKEN) return;
      
      safelyCallMixpanel('identify', userId);
      if (traits) {
        safelyCallMixpanel('people.set', {
          ...traits,
          '$last_login': new Date().toISOString(),
        });
      }
      console.log(`[Analytics] User identified: ${userId}`);
    } catch (error) {
      console.error(`[Analytics] Error identifying user ${userId}:`, error);
    }
  },

  // Reset user identity on logout
  reset: () => {
    try {
      // Return early if no token
      if (!MIXPANEL_TOKEN) return;
      
      safelyCallMixpanel('reset');
      console.log(`[Analytics] User identity reset`);
    } catch (error) {
      console.error(`[Analytics] Error resetting user:`, error);
    }
  },
  
  // Track CTA clicks in a standardized way
  trackCTA: (ctaType: string, ctaLocation: string, properties?: Record<string, any>) => {
    try {
      const ctaProps = {
        cta_type: ctaType,
        cta_location: ctaLocation,
        ...properties,
      };
      
      console.log(`[Analytics] CTA Clicked: ${ctaType} at ${ctaLocation}`, ctaProps);
      
      // Return early if no token
      if (!MIXPANEL_TOKEN) return;
      
      safelyCallMixpanel('track', 'CTA Clicked', ctaProps);
    } catch (error) {
      console.error(`[Analytics] Error tracking CTA:`, error);
    }
  },
  
  // Track user engagement with features
  trackEngagement: (feature: string, action: string, properties?: Record<string, any>) => {
    try {
      const engagementProps = {
        feature,
        action,
        ...properties,
      };
      
      console.log(`[Analytics] User Engagement: ${feature} - ${action}`, engagementProps);
      
      // Return early if no token
      if (!MIXPANEL_TOKEN) return;
      
      safelyCallMixpanel('track', 'Feature Engagement', engagementProps);
    } catch (error) {
      console.error(`[Analytics] Error tracking engagement:`, error);
    }
  }
};

export default Analytics;
